// Catalog API response mappers
// Transforms backend snake_case to frontend camelCase

import type { MasterProduct, SupplierListing, FulfillmentOption } from '../types'
import { API_URL } from '../config/api-mode'

/** Resolve an image URL returned by the API. Paths starting with "/api/" are
 *  server-proxied CDN images (e.g. Wolseley) and must be rooted at the API
 *  origin, not the frontend origin. */
function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null
  if (url.startsWith('/api/')) return `${API_URL}${url}`
  return url
}

// Default stock quantity when data is not available from API
// Default to 0 (unknown/unavailable) - live stock API will provide real values
const DEFAULT_STOCK_QUANTITY = 0

/**
 * Synonym / Did-you-mean resolution outcome for one search response (Epic 3).
 */
export interface SearchQueryInfo {
  original: string
  used: string
  wasCorrected: boolean
  correctionHighlighted: string | null
  synonymFuzzyCorrected: boolean
  synonymFuzzyUsed: string | null
  keywordCategoryFuzzyCorrected: boolean
  keywordCategoryFuzzyUsed: string | null
}

/**
 * Transform master product search API response to frontend type
 */
export function mapMasterProductSearchResponse(response: any): {
  products: MasterProduct[]
  totalProducts: number
  allMasterIds: string[]
  masterIdsBySupplier: Record<number, string[]>
  searchQuery: SearchQueryInfo
} {
  const fallbackSearchQuery: SearchQueryInfo = {
    original: '',
    used: '',
    wasCorrected: false,
    correctionHighlighted: null,
    synonymFuzzyCorrected: false,
    synonymFuzzyUsed: null,
    keywordCategoryFuzzyCorrected: false,
    keywordCategoryFuzzyUsed: null,
  }

  if (!response || !Array.isArray(response.results)) {
    return {
      products: [],
      totalProducts: 0,
      allMasterIds: [],
      masterIdsBySupplier: {},
      searchQuery: fallbackSearchQuery,
    }
  }

  const rawAllIds = Array.isArray(response.all_master_ids)
    ? response.all_master_ids
    : []

  // Backend ships `master_ids_by_supplier` keyed by stringified supplier
  // IDs (some Django cache backends coerce int keys). Normalise back to
  // numeric keys with stringified master IDs (matching MasterProduct.id).
  const rawMapping =
    response.master_ids_by_supplier && typeof response.master_ids_by_supplier === 'object'
      ? response.master_ids_by_supplier
      : {}
  const masterIdsBySupplier: Record<number, string[]> = {}
  for (const supplierKey of Object.keys(rawMapping)) {
    const supplierId = parseInt(supplierKey, 10)
    if (!Number.isFinite(supplierId)) continue
    const ids = rawMapping[supplierKey]
    if (!Array.isArray(ids)) continue
    masterIdsBySupplier[supplierId] = ids.map((value: string | number) =>
      String(value),
    )
  }

  // Synonym / Did-you-mean resolution outcome (Epic 3). Backend always
  // returns this block; defensively fall back to neutral values if a
  // legacy endpoint somehow omits it.
  const rawSearchQuery =
    response.search_query && typeof response.search_query === 'object'
      ? response.search_query
      : null
  const searchQuery: SearchQueryInfo = rawSearchQuery
    ? {
        original: String(rawSearchQuery.original ?? ''),
        used: String(rawSearchQuery.used ?? ''),
        wasCorrected: Boolean(rawSearchQuery.was_corrected),
        correctionHighlighted:
          typeof rawSearchQuery.correction_highlighted === 'string'
            ? rawSearchQuery.correction_highlighted
            : null,
        synonymFuzzyCorrected: Boolean(rawSearchQuery.synonym_fuzzy_corrected),
        synonymFuzzyUsed:
          typeof rawSearchQuery.synonym_fuzzy_used === 'string'
            ? rawSearchQuery.synonym_fuzzy_used
            : null,
        keywordCategoryFuzzyCorrected: Boolean(
          rawSearchQuery.keyword_category_fuzzy_corrected,
        ),
        keywordCategoryFuzzyUsed:
          typeof rawSearchQuery.keyword_category_fuzzy_used === 'string'
            ? rawSearchQuery.keyword_category_fuzzy_used
            : null,
      }
    : fallbackSearchQuery

  return {
    products: response.results.map(mapMasterProductListItem),
    totalProducts: response.count || 0,
    // Stringify master IDs so they match `MasterProduct.id` which is a
    // string in the frontend type — keeps comparisons cheap and avoids
    // sprinkling parseInt across the consumers.
    allMasterIds: rawAllIds.map((value: string | number) => String(value)),
    masterIdsBySupplier,
    searchQuery,
  }
}

/**
 * Transform single master product list item
 */
function mapMasterProductListItem(item: any): MasterProduct {
  if (!item) {
    throw new Error('Invalid master product item: item is null or undefined')
  }

  return {
    id: item.id,
    displayName: item.display_name || '',
    canonicalMpn: item.canonical_mpn || null,
    brand: item.brand || null,
    description: item.description || '',
    imageUrl: resolveImageUrl(item.image_url),
    unitOfMeasure: null,  // UOM not included in search results - shown per supplier in detail
    isPreferred: Boolean(item.is_preferred),
    isApprovalRequired: Boolean(item.is_approval_required),
    supplierCount: item.supplier_count || 0,
    lowestPrice: item.lowest_price ? parseFloat(item.lowest_price) : 0,
    lowestPriceSupplierName: item.lowest_price_supplier_name || null,
    nearestDistance: item.nearest_distance ? parseFloat(item.nearest_distance) : null,
    isInStock: Boolean(item.is_in_stock),
    // Absent (older payloads / non-search endpoints) → treat as reachable.
    hasNearbyBranch: item.has_nearby_branch !== false,
    supplierListings: [], // Populated in detail view
  }
}

/**
 * Transform master product detail API response
 */
export function mapMasterProductDetail(response: any): MasterProduct {
  if (!response) {
    throw new Error('Invalid master product detail: response is null or undefined')
  }

  const supplierListings = Array.isArray(response.supplier_listings)
    ? response.supplier_listings.map(mapSupplierListing)
    : []

  // Calculate nearest distance from supplier listings
  const distances = supplierListings
    .filter((l: SupplierListing) => l.distanceMiles != null)
    .map((l: SupplierListing) => l.distanceMiles as number)
  const nearestDistance = distances.length > 0 ? Math.min(...distances) : null

  return {
    id: response.id,
    displayName: response.display_name || '',
    canonicalMpn: response.canonical_mpn || null,
    brand: response.brand || null,
    description: response.description || '',
    imageUrl: resolveImageUrl(response.image_url),
    images: response.images
      ? response.images.map((img: any) => ({ ...img, url: resolveImageUrl(img.url) }))
      : undefined,
    unitOfMeasure: null,  // UOM shown per supplier listing, not at master product level
    isPreferred: Boolean(response.is_preferred),
    isApprovalRequired: Boolean(response.is_approval_required),
    supplierCount: response.supplier_count || supplierListings.length,
    lowestPrice: response.lowest_price ? parseFloat(response.lowest_price) : 0,
    // Detail endpoint doesn't compute this — the page has full supplier
    // listings and derives the display supplier itself; nulling keeps the
    // shape consistent for TS.
    lowestPriceSupplierName: response.lowest_price_supplier_name || null,
    nearestDistance,
    isInStock: Boolean(response.is_in_stock),
    // Detail endpoint doesn't emit this; the page derives the "no nearby
    // branch" state from the per-listing live-stock response instead.
    hasNearbyBranch: response.has_nearby_branch !== false,
    supplierListings,
  }
}

/**
 * Transform supplier listing from API response
 */
function mapSupplierListing(listing: any): SupplierListing {
  if (!listing) {
    throw new Error('Invalid supplier listing: listing is null or undefined')
  }

  // Get stock quantity from listing (from API or mock data)
  const stockQuantity = listing.stock_quantity ?? DEFAULT_STOCK_QUANTITY
  const collectionStock = listing.collection_stock ?? stockQuantity
  const deliveryStock = listing.delivery_stock ?? stockQuantity
  const canCollect = listing.can_collect !== false
  const canDeliver = listing.can_deliver !== false

  // Map fulfillment options from API response
  const fulfillmentOptions: FulfillmentOption[] = Array.isArray(listing.fulfillment_options)
    ? listing.fulfillment_options.map((opt: any) => ({
        type: opt.type as 'pickup' | 'delivery',
        available: Boolean(opt.available),
        stockQuantity: opt.stock_quantity ?? stockQuantity,
        stockStatus: (opt.stock_status || 'in_stock') as 'in_stock' | 'low_stock' | 'out_of_stock',
        earliestDate: opt.earliest_date,
      }))
    : [
        {
          type: 'pickup' as const,
          available: canCollect && collectionStock > 0,
          stockQuantity: canCollect ? collectionStock : 0,
          stockStatus: collectionStock > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: listing.earliest_collection ?? undefined,
        },
        {
          type: 'delivery' as const,
          available: canDeliver && deliveryStock > 0,
          stockQuantity: canDeliver ? deliveryStock : 0,
          stockStatus: deliveryStock > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: listing.earliest_delivery ?? undefined,
        },
      ]

  // Determine stock status from quantity
  let stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock' = 'in_stock'
  if (listing.stock_status) {
    stockStatus = listing.stock_status
  } else if (stockQuantity === 0) {
    stockStatus = 'out_of_stock'
  } else if (stockQuantity < 10) {
    stockStatus = 'low_stock'
  }

  return {
    productId: listing.product_id,
    branchId: listing.branch_id || null,
    supplierId: listing.supplier_id,
    productName: listing.product_name || '',
    productDescription: listing.product_description || '',
    supplierName: listing.supplier_name || '',
    branchName: listing.branch_name || '',
    supplierProductCode: listing.supplier_product_code || '',
    manufacturerCode: listing.manufacturer_code || null,
    brand: listing.brand || null,
    price: listing.price ? parseFloat(listing.price) : 0,
    unitOfMeasure: listing.unit_of_measure || 'Each',
    quantityMultiple: listing.quantity_multiple ?? 1,
    stockStatus,
    stockQuantity,
    branchStock: listing.branch_stock ?? null,
    dcStock: listing.dc_stock ?? null,
    totalStock: listing.total_stock ?? null,
    canCollect,
    canDeliver,
    distanceMiles: listing.distance_miles != null ? parseFloat(listing.distance_miles) : null,
    isPreferred: Boolean(listing.is_preferred),
    isRestricted: Boolean(listing.is_restricted),
    isVanStock: Boolean(listing.is_van_stock),
    imageUrl: resolveImageUrl(listing.image_url),
    // Backend-authoritative: this supplier has no branch in range of the
    // user's postcode. Drives the reliable "No nearby branch" tag.
    noNearbyBranch: listing.no_nearby_branch === true,
    fulfillmentOptions,
  }
}

/**
 * Transform raw product (supplier product) detail API response
 */
export function mapRawProductDetail(response: any): any {
  if (!response) {
    throw new Error('Invalid raw product detail: response is null or undefined')
  }

  // Get stock quantity from response
  const stockQuantity = response.stock_quantity ?? DEFAULT_STOCK_QUANTITY
  const collectionStock = response.collection_stock ?? stockQuantity
  const deliveryStock = response.delivery_stock ?? stockQuantity
  const canCollect = response.can_collect !== false
  const canDeliver = response.can_deliver !== false

  // Map fulfillment options from API response
  const fulfillmentOptions: FulfillmentOption[] = Array.isArray(response.fulfillment_options)
    ? response.fulfillment_options.map((opt: any) => ({
        type: opt.type as 'pickup' | 'delivery',
        available: Boolean(opt.available),
        stockQuantity: opt.stock_quantity ?? stockQuantity,
        stockStatus: (opt.stock_status || 'in_stock') as 'in_stock' | 'low_stock' | 'out_of_stock',
        earliestDate: opt.earliest_date,
      }))
    : [
        {
          type: 'pickup' as const,
          available: canCollect && collectionStock > 0,
          stockQuantity: canCollect ? collectionStock : 0,
          stockStatus: collectionStock > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: response.earliest_collection ?? undefined,
        },
        {
          type: 'delivery' as const,
          available: canDeliver && deliveryStock > 0,
          stockQuantity: canDeliver ? deliveryStock : 0,
          stockStatus: deliveryStock > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: response.earliest_delivery ?? undefined,
        },
      ]

  // Determine stock status from quantity
  let stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock' = 'in_stock'
  if (response.stock_status) {
    stockStatus = response.stock_status
  } else if (stockQuantity === 0) {
    stockStatus = 'out_of_stock'
  } else if (stockQuantity < 10) {
    stockStatus = 'low_stock'
  }

  return {
    productId: response.id,
    supplierId: response.supplier_id,
    supplierName: response.supplier_name || '',
    productName: response.name || '',
    productDescription: response.description || '',
    supplierProductCode: response.supplier_product_code || '',
    categoryL1: response.category_name_l1 || '',
    categoryL2: response.category_name_l2 || '',
    imageUrl: resolveImageUrl(response.image_url),
    images: response.images
      ? response.images.map((img: any) => ({ ...img, url: resolveImageUrl(img.url) }))
      : undefined,
    unitOfMeasure: response.unit_of_measure || 'Each',
    quantityMultiple: response.quantity_multiple ?? 1,
    price: response.catalogue_price ? parseFloat(response.catalogue_price) : 0,
    manufacturerCode: response.manufacturer_code || '',
    brand: response.brand || null,
    masterProductId: response.master_product_id || null,
    masterProductName: response.master_product_name || null,
    branchId: response.branch_id || null,
    branchName: response.branch_name || '',
    branch: response.branch || null,
    stockStatus,
    stockQuantity,
    branchStock: response.branch_stock ?? null,
    dcStock: response.dc_stock ?? null,
    totalStock: response.total_stock ?? null,
    canCollect,
    canDeliver,
    distanceMiles: response.distance_miles != null ? parseFloat(response.distance_miles) : null,
    fulfillmentOptions,
    isPreferred: Boolean(response.is_preferred),
    isRestricted: Boolean(response.is_restricted),
    isVanStock: Boolean(response.is_van_stock),
  }
}
